import {Button, Select, SpinningLoader} from '@/components';
import {WebhookDeliveryEvent} from '@/types/WebhookDelivery';

import {DeliveryRow} from './DeliveryRow';
import type {DeliveriesListProps} from './types';

const EVENT_OPTIONS = Object.values(WebhookDeliveryEvent).map((event) => ({
  key: event,
  value: event,
}));

const STATUS_OPTIONS = [
  {key: 'Success', value: 'SUCCESS'},
  {key: 'Failed', value: 'FAILED'},
];

export const DeliveriesList = ({
  deliveries,
  selectedKey,
  onSelect,
  filters,
  setFilters,
  hasMore,
  isLoading,
  error,
  loadMore,
}: DeliveriesListProps) => (
  <div className="flex flex-col">
    <div className="flex flex-wrap gap-2 p-3 items-center border-b border-gray-100 dark:border-dark-borderMid">
      <Select
        options={EVENT_OPTIONS}
        value={filters.event ?? ''}
        placeholder="All events"
        data-testid="event-filter"
        className="flex-1 min-w-[140px]"
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilters({...filters, event: e.target.value || undefined})
        }
      />
      <Select
        options={STATUS_OPTIONS}
        value={filters.status ?? ''}
        placeholder="All statuses"
        data-testid="status-filter"
        className="flex-1 min-w-[140px]"
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          setFilters({
            ...filters,
            status:
              (e.target.value as DeliveriesListProps['filters']['status']) ||
              undefined,
          })
        }
      />
    </div>

    {error && (
      <p
        className="px-3 py-3 text-sm text-red-600 dark:text-red-400"
        data-testid="deliveries-error"
      >
        Failed to load deliveries. Please try again.
      </p>
    )}

    <div className="lg:max-h-[500px] overflow-y-auto">
      {deliveries.length === 0 && !isLoading && !error && (
        <p
          className="px-3 py-8 text-center text-sm text-gray-400 dark:text-gray-500"
          data-testid="deliveries-empty"
        >
          No deliveries found.
        </p>
      )}
      {deliveries.map((delivery) => (
        <DeliveryRow
          key={`${delivery.webhookId}-${delivery.attempt}`}
          delivery={delivery}
          isActive={selectedKey === `${delivery.webhookId}-${delivery.attempt}`}
          onSelect={onSelect}
        />
      ))}
      {isLoading && (
        <div
          className="flex items-center justify-center py-4"
          data-testid="deliveries-loading"
        >
          <SpinningLoader size={8} />
        </div>
      )}
    </div>

    {hasMore && (
      <div className="flex justify-center p-3 border-t border-gray-100 dark:border-dark-borderMid">
        <Button
          variant="outline"
          onClick={loadMore}
          disabled={isLoading}
          data-testid="load-more-btn"
          className="text-sm"
        >
          {isLoading ? 'Loading…' : 'Load more'}
        </Button>
      </div>
    )}
  </div>
);
